import {
  type AdminQuoteDraft,
  type AdminQuoteGuest,
  koreanCalendarDate,
  type QuoteDestination,
} from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useId, useState } from 'react';
import { formatWon } from '@/shared/lib';
import { Button, ContentCard, cn, FormField, Label, tableStyles } from '@/shared/ui';
import {
  type DraftForm,
  type DraftLineError,
  type DraftLineForm,
  draftFormFromDraft,
  emptyDraftLine,
  parseDraftForm,
  previewDraftAmounts,
  resetShippingToSuggested,
  savedShippingDestinationIds,
  serverDraftErrors,
} from '../model/draft-form';
import { useSaveQuoteDraft } from '../model/use-save-quote-draft';
import { IssueQuotePanel } from './issue-quote-panel';
import { QuoteLineRow } from './quote-line-row';
import { QuoteShippingLineRow } from './quote-shipping-line-row';

export interface QuoteDraftEditorProps {
  readonly quoteRequestId: string;
  readonly draft: AdminQuoteDraft;
  /** 견적 배송지 — 배송비 묶음의 행 순서·요약. 배송지가 없는 견적은 빈 배열. */
  readonly destinations: readonly QuoteDestination[];
  /**
   * 충돌 뒤 운영자가 "최신 초안 다시 불러오기"를 고르면 부른다 — 상위가 상세를 다시 불러오고
   * 편집기를 새로 시작한다(지금 입력은 사라진다). 자동으로 부르지 않는다.
   */
  readonly onReloadLatest: () => void;
  /** 한국 오늘(YYYY-MM-DD) — 기본 만료일·지난 날짜 검사 기준. 기본은 지금 시각의 한국 날짜, 스펙이 고정한다. */
  readonly today?: string;
  /** 비회원 접수 요약 — 발행 영역이 링크 전달 안내를 고른다(비회원 견적 설계 G14). 회원 견적은 null. */
  readonly guest?: AdminQuoteGuest | null;
}

const OUTDATED_NOTICE =
  '다른 곳에서 초안이 바뀌었습니다. 입력은 그대로 두었습니다 — 최신 초안을 다시 불러오면 지금 입력은 사라집니다.';
const TERM_CLASS = 'text-muted-foreground';
// 줄 칸(QuoteLineRow·QuoteShippingLineRow)의 px-2 와 맞춘다.
const TH_CLASS = cn(tableStyles.th, 'px-2');
const TEXTAREA_CLASS =
  'w-full rounded-2xl border border-input bg-background px-4 py-2 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** 줄 키를 뺀 폼 값 — 저장 뒤 변경 여부 비교용. */
const snapshotOf = (form: DraftForm): string =>
  JSON.stringify({
    ...form,
    lines: form.lines.map((line) => [line.name, line.quantity, line.unitPrice]),
    shippingLines: form.shippingLines.map((line) => [line.destinationId, line.unitPrice]),
  });

/**
 * 서버 원본의 저장 스냅샷 — 빈 만료일·추천값으로 채운 배송비 행은 "저장 안 된 변경"이라 임시 저장 전까지 발행이 잠긴다.
 */
const savedSnapshotOf = (form: DraftForm, draft: AdminQuoteDraft): string => {
  const savedIds = savedShippingDestinationIds(draft);
  return snapshotOf({
    ...form,
    validUntil: draft.validUntil ?? '',
    shippingLines: form.shippingLines.filter((line) => savedIds.has(line.destinationId)),
  });
};

const movedLines = (
  lines: readonly DraftLineForm[],
  from: number,
  to: number,
): readonly DraftLineForm[] =>
  lines.map((line, index) => {
    if (index === from) return lines[to] ?? line;
    if (index === to) return lines[from] ?? line;
    return line;
  });

/**
 * 초안 편집기(견적 설계 §6-3) — 줄 편집은 '상품'(추가·삭제·정렬)과 '배송비'(배송지마다 한 행, 금액만, SUPER-86 §6-2)
 * 두 묶음, 조건 3칸·만료일·특약, 임시 저장, 발행.
 * 상품 금액·배송비·합계·공급가액·부가세는 표시용 로컬 계산이고 저장값은 서버 응답(draft)을 믿는다.
 * 배송비 줄이 저장되지 않은 배송지는 추천 배송비(`suggestedShippingFees`)로 채워 '저장 안 됨' 으로 표시하고, [기본값으로] 는
 * 고친 배송비를 추천값으로 되돌린다.
 * 만료일이 비어 있는 초안은 오늘+7일을 채우되, 저장 스냅샷은 서버 원본(빈 값)이라 채운 값은 "저장 안 된 변경"으로
 * 잡혀 임시 저장 전까지 발행이 잠긴다.
 * 저장·발행은 이 편집기가 불러왔거나 마지막으로 저장한 초안의 토큰(`updatedAt`)을 싣는다 — 다른 운영자가 그 사이 저장했으면
 * 서버가 CONFLICT 로 거절하고, 편집 중인 줄을 지우지 않은 채 "최신 초안 다시 불러오기"를 운영자가 고르게 한다.
 */
export const QuoteDraftEditor = ({
  quoteRequestId,
  draft,
  destinations,
  onReloadLatest,
  today = koreanCalendarDate(new Date()),
  guest = null,
}: QuoteDraftEditorProps): ReactElement => {
  const [form, setForm] = useState<DraftForm>(() => draftFormFromDraft(draft, destinations, today));
  const [savedSnapshot, setSavedSnapshot] = useState<string>(() =>
    savedSnapshotOf(draftFormFromDraft(draft, destinations, today), draft),
  );
  /** 서버에 배송비 줄이 저장된 배송지 — '저장 안 됨' 표시 기준. 저장이 성공하면 모든 배송지로 바뀐다. */
  const [savedShippingIds, setSavedShippingIds] = useState(() =>
    savedShippingDestinationIds(draft),
  );
  /** 낙관적 동시성 토큰 — 불러온 초안의 것으로 시작해 내 저장이 성공하면 그 결과 초안의 것으로 바뀐다. */
  const [draftToken, setDraftToken] = useState(draft.updatedAt);
  const [lineErrors, setLineErrors] = useState<Readonly<Record<string, DraftLineError>>>({});
  const [shippingErrors, setShippingErrors] = useState<Readonly<Record<string, string>>>({});
  const [clientError, setClientError] = useState<string | null>(null);
  const termsId = useId();
  const { save, isPending, formError, isOutdated } = useSaveQuoteDraft();
  const preview = previewDraftAmounts(form);
  const destinationById = new Map(destinations.map((destination) => [destination.id, destination]));
  const hasShipping = form.shippingLines.length > 0;
  const isDirty = snapshotOf(form) !== savedSnapshot;
  const message = clientError ?? formError;

  const updateLine = (key: string, patch: Partial<Omit<DraftLineForm, 'key'>>): void =>
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    }));
  const addLine = (): void =>
    setForm((current) => ({ ...current, lines: [...current.lines, emptyDraftLine()] }));
  const removeLine = (key: string): void =>
    setForm((current) => ({ ...current, lines: current.lines.filter((line) => line.key !== key) }));
  const moveLine = (index: number, offset: number): void =>
    setForm((current) => {
      const target = index + offset;
      if (target < 0 || target >= current.lines.length) return current;
      return { ...current, lines: movedLines(current.lines, index, target) };
    });
  const updateShippingFee = (destinationId: string, unitPrice: string): void =>
    setForm((current) => ({
      ...current,
      shippingLines: current.shippingLines.map((line) =>
        line.destinationId === destinationId ? { ...line, unitPrice } : line,
      ),
    }));
  const resetShipping = (): void =>
    setForm((current) => ({
      ...current,
      shippingLines: resetShippingToSuggested(current.shippingLines, draft.suggestedShippingFees),
    }));
  const setField = (
    field: Exclude<keyof DraftForm, 'lines' | 'shippingLines'>,
    value: string,
  ): void => setForm((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setLineErrors({});
    setShippingErrors({});
    setClientError(null);

    const parsed = parseDraftForm(
      form,
      { quoteRequestId, versionId: draft.versionId, expectedDraftUpdatedAt: draftToken },
      today,
    );

    if (!parsed.ok) {
      setLineErrors(parsed.lineErrors);
      setShippingErrors(parsed.shippingErrors);
      setClientError(parsed.formError);
      return;
    }

    const outcome = await save(parsed.input);

    if (outcome.ok) {
      setSavedSnapshot(snapshotOf(form));
      setSavedShippingIds(new Set(form.shippingLines.map((line) => line.destinationId)));
      setDraftToken(outcome.draftUpdatedAt);
      return;
    }

    // 서버 줄 칸 오류(lines.{i}.{칸}) — 보낸 폼의 순서로 되짚어 그 줄·배송지 행에 붙인다.
    const serverErrors = serverDraftErrors(form, outcome.fieldErrors);
    setLineErrors(serverErrors.lineErrors);
    setShippingErrors(serverErrors.shippingErrors);
  };

  return (
    <ContentCard title={`초안 편집 — 차`} className="mt-6">
      <form
        aria-label="초안 편집"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        className="flex flex-col gap-4"
      >
        <h3 className="text-sm font-semibold">상품</h3>
        <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
          <table aria-label="품목 줄" className={tableStyles.table}>
            <thead className={tableStyles.head}>
              <tr>
                <th className={TH_CLASS}>품목</th>
                <th className={TH_CLASS}>수량</th>
                <th className={TH_CLASS}>단가(원)</th>
                <th className={cn(TH_CLASS, 'text-right')}>금액</th>
                <th className={TH_CLASS}>
                  <span className="sr-only">순서·삭제</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((line, index) => (
                <QuoteLineRow
                  key={line.key}
                  line={line}
                  index={index}
                  count={form.lines.length}
                  amount={preview.lineAmounts[index] ?? null}
                  error={lineErrors[line.key]}
                  disabled={isPending}
                  onChange={(patch) => updateLine(line.key, patch)}
                  onMoveUp={() => moveLine(index, -1)}
                  onMoveDown={() => moveLine(index, 1)}
                  onRemove={() => removeLine(line.key)}
                />
              ))}
            </tbody>
          </table>
        </div>
        {form.lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            품목 줄이 없습니다. 발행하려면 한 줄 이상 필요합니다.
          </p>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          disabled={isPending}
          onClick={addLine}
        >
          줄 추가
        </Button>

        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">배송비</h3>
          {hasShipping ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isPending}
              onClick={resetShipping}
            >
              기본값으로
            </Button>
          ) : null}
        </div>
        {hasShipping ? (
          <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
            <table aria-label="배송비 줄" className={tableStyles.table}>
              <thead className={tableStyles.head}>
                <tr>
                  <th className={TH_CLASS}>배송지</th>
                  <th className={TH_CLASS}>지역</th>
                  <th className={TH_CLASS}>배송비(원, 부가세 포함)</th>
                  <th className={cn(TH_CLASS, 'text-right')}>금액</th>
                </tr>
              </thead>
              <tbody>
                {form.shippingLines.map((line, index) => {
                  const destination = destinationById.get(line.destinationId);
                  return destination === undefined ? null : (
                    <QuoteShippingLineRow
                      key={line.destinationId}
                      destination={destination}
                      index={index}
                      unitPrice={line.unitPrice}
                      amount={preview.shippingAmounts[index] ?? null}
                      unsaved={!savedShippingIds.has(line.destinationId)}
                      error={shippingErrors[line.destinationId]}
                      disabled={isPending}
                      onChange={(unitPrice) => updateShippingFee(line.destinationId, unitPrice)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            배송지가 없는 견적이라 배송비 줄이 없습니다. 배송지는 주문할 때 받습니다.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <section aria-label="금액 미리 계산" className="rounded-md border p-3">
            <h3 className="text-sm font-semibold">미리 계산</h3>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm tabular-nums">
              <dt className={TERM_CLASS}>상품 금액</dt>
              <dd className="text-right">{formatWon(preview.productAmount)}</dd>
              {hasShipping ? <dt className={TERM_CLASS}>배송비</dt> : null}
              {hasShipping ? (
                <dd className="text-right">{formatWon(preview.shippingAmount)}</dd>
              ) : null}
              <dt className={TERM_CLASS}>합계</dt>
              <dd className="text-right">{formatWon(preview.totalAmount)}</dd>
              <dt className={TERM_CLASS}>공급가액</dt>
              <dd className="text-right">{formatWon(preview.supplyAmount)}</dd>
              <dt className={TERM_CLASS}>부가세</dt>
              <dd className="text-right">{formatWon(preview.vatAmount)}</dd>
            </dl>
            <p className="mt-2 text-xs text-muted-foreground">
              {preview.isComplete
                ? '표시용 계산입니다. 임시 저장하면 서버가 다시 계산합니다.'
                : '정수가 아닌 줄은 뺀 값입니다. 임시 저장하면 서버가 다시 계산합니다.'}
            </p>
          </section>
          <section aria-label="저장된 금액" className="rounded-md border p-3">
            <h3 className="text-sm font-semibold">저장된 금액</h3>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm tabular-nums">
              <dt className={TERM_CLASS}>상품 금액</dt>
              <dd className="text-right">{formatWon(draft.productAmount)}</dd>
              {hasShipping ? <dt className={TERM_CLASS}>배송비</dt> : null}
              {hasShipping ? (
                <dd className="text-right">{formatWon(draft.shippingAmount)}</dd>
              ) : null}
              <dt className={TERM_CLASS}>합계</dt>
              <dd className="text-right">{formatWon(draft.totalAmount)}</dd>
              <dt className={TERM_CLASS}>공급가액</dt>
              <dd className="text-right">{formatWon(draft.supplyAmount)}</dd>
              <dt className={TERM_CLASS}>부가세</dt>
              <dd className="text-right">{formatWon(draft.vatAmount)}</dd>
            </dl>
          </section>
        </div>

        <FormField
          label="제작 착수 시점"
          description="발행 시 필수"
          value={form.productionStartNote}
          disabled={isPending}
          onChange={(event) => setField('productionStartNote', event.target.value)}
        />
        <FormField
          label="납기"
          description="발행 시 필수"
          value={form.deliveryNote}
          disabled={isPending}
          onChange={(event) => setField('deliveryNote', event.target.value)}
        />
        <FormField
          label="공급 방법"
          description="발행 시 필수"
          value={form.deliveryMethodNote}
          disabled={isPending}
          onChange={(event) => setField('deliveryMethodNote', event.target.value)}
        />
        <FormField
          label="견적서 유효기한(만료일)"
          type="date"
          min={today}
          description="발행 시 필수. 한국 시간 오늘 이후, 그날 끝까지 유효합니다."
          value={form.validUntil}
          disabled={isPending}
          onChange={(event) => setField('validUntil', event.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={termsId}>특약 사항</Label>
          <textarea
            id={termsId}
            rows={3}
            value={form.specialTerms}
            disabled={isPending}
            className={TEXTAREA_CLASS}
            placeholder="선택 — 견적서에 그대로 실립니다."
            onChange={(event) => setField('specialTerms', event.target.value)}
          />
        </div>

        {message ? (
          <p role="alert" className="text-sm text-destructive">
            {message}
          </p>
        ) : null}

        {isOutdated ? (
          <div
            role="status"
            className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
          >
            <p>{OUTDATED_NOTICE}</p>
            <Button type="button" variant="outline" size="sm" onClick={onReloadLatest}>
              최신 초안 다시 불러오기
            </Button>
          </div>
        ) : null}

        <Button type="submit" isLoading={isPending} className="self-start">
          임시 저장
        </Button>

        <IssueQuotePanel
          quoteRequestId={quoteRequestId}
          versionId={draft.versionId}
          expectedDraftUpdatedAt={draftToken}
          isDirty={isDirty}
          onReloadLatest={onReloadLatest}
          guest={guest}
        />
      </form>
    </ContentCard>
  );
};
